/**
 * Stand-in illustrations, drawn as inline SVG data URIs.
 *
 * These exist so a listing is never a blank grey box before its photos are
 * taken. Anything produced here is clearly a drawing, and the UI labels it as
 * one — a customer must never mistake a placeholder for the actual item.
 */

import type { BodyType, PartCategory } from '@/types'
import { photoUrl, isStockPhoto } from '@/lib/photos'

const encode = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`

/** True when a picture came out of this module rather than a camera. */
export function isIllustration(src: string | undefined | null): boolean {
  return typeof src === 'string' && src.startsWith('data:image/svg+xml')
}

/* --------------------------- donor vehicle -------------------------- */

interface BodySpec { body: string; glass: string; wheelY: number; wheelR: number }

const BODY_ART: Record<BodyType, BodySpec> = {
  hatch: {
    body: 'M12 63C12 55 14 52 18 51L30 38C32 36 34 35 37 35H58C62 35 65 37 67 40L76 50 85 52C89 53 90 57 90 63Z',
    glass: 'M24 49 34 39H45V49ZM48 39H57C59 39 60.5 40 62 42L66 49H48Z', wheelY: 63, wheelR: 8.6,
  },
  sedan: {
    body: 'M10 63C10 56 12 53 16 52L23 50.5 33 39C35 36.5 37 36 40 36H58C62 36 64 38 66 40L75 50 85 52C89 53 90 57 90 63Z',
    glass: 'M27 49.5 36 40H46V49.5ZM49 40H57C59 40 60 41 61 42.5L65 49.5H49Z', wheelY: 63, wheelR: 8.6,
  },
  suv: {
    body: 'M11 62C11 52 13 48 17 47L27 33C29 30.5 31 30 34 30H60C64 30 66 32 68 34L77 47 86 49C90 50 91 55 91 62Z',
    glass: 'M23 45 32 34H45V45ZM48 34H59C61 34 62 35 63.5 36.5L67 45H48Z', wheelY: 62, wheelR: 9.6,
  },
  van: {
    body: 'M11 62C11 46 12 35 17 33H62C66 33 68 35 70 38L78 47 87 49C90 50 91 55 91 62Z',
    glass: 'M18 45V37H32V45ZM35 37H47V45H35ZM50 37H58C60 37 61 38 62 39.5L66 45H50Z', wheelY: 62, wheelR: 8.8,
  },
}

const PAINTS: readonly (readonly [RegExp, string, string])[] = [
  [/pearl|white|ivory|snow/i, '#E9ECEF', '#C9D0D7'],
  [/gun ?met|charcoal|graphite/i, '#6D767F', '#525A62'],
  [/silver|grey|gray/i, '#BFC7CE', '#9AA4AD'],
  [/black|noir/i, '#343A41', '#22272C'],
  [/navy|blue/i, '#2E63A6', '#224E84'],
  [/red|crimson|maroon|burgundy/i, '#B2372F', '#8B2A23'],
  [/green|emerald/i, '#2B7A4F', '#1F603D'],
  [/orange|copper|bronze/i, '#C4703A', '#9B562C'],
  [/gold|yellow|champagne/i, '#C9A23A', '#A07D22'],
  [/brown|beige|tan/i, '#8B7357', '#6D5943'],
]

export function paintFor(colour: string | undefined): readonly [string, string] {
  for (const [re, main, shade] of PAINTS) if (re.test(colour ?? '')) return [main, shade]
  return ['#BFC7CE', '#9AA4AD']
}

/** Painted in the car's real colour, so the card matches the car in the yard. */
export function carArt(body: BodyType, colour?: string): string {
  const spec = BODY_ART[body] ?? BODY_ART.hatch
  const [main, shade] = paintFor(colour)
  const wheel = (cx: number) =>
    `<circle cx="${cx}" cy="${spec.wheelY}" r="${spec.wheelR}" fill="#2A2D31"/>` +
    `<circle cx="${cx}" cy="${spec.wheelY}" r="${(spec.wheelR * 0.46).toFixed(1)}" fill="#A3ABA5"/>` +
    `<circle cx="${cx}" cy="${spec.wheelY}" r="${(spec.wheelR * 0.18).toFixed(1)}" fill="#6E766F"/>`

  return encode(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="360" height="360">` +
      `<defs><linearGradient id="bg" x1="0" y1="0" x2=".3" y2="1"><stop offset="0" stop-color="#F2F4F2"/><stop offset="1" stop-color="#CED5D0"/></linearGradient>` +
      `<linearGradient id="p" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${main}"/><stop offset="1" stop-color="${shade}"/></linearGradient></defs>` +
      `<rect width="100" height="100" fill="url(#bg)"/>` +
      `<ellipse cx="50" cy="${spec.wheelY + spec.wheelR + 2}" rx="40" ry="3.6" fill="rgba(26,32,28,.14)"/>` +
      `<path d="${spec.body}" fill="url(#p)"/>` +
      `<path d="${spec.glass}" fill="#DDE4DF" stroke="${shade}" stroke-width="1" stroke-linejoin="round"/>` +
      `<path d="${spec.body}" fill="none" stroke="rgba(22,28,24,.32)" stroke-width="1.1" stroke-linejoin="round"/>` +
      wheel(31) + wheel(69) +
    `</svg>`,
  )
}

/* ------------------------------ parts ------------------------------- */

const GLYPHS: Record<string, string> = {
  battery: '<rect x="11" y="30" width="64" height="32" rx="4"/><path d="M24 30v32M37 30v32M50 30v32M63 30v32"/><rect x="19" y="23" width="11" height="7" rx="2"/><rect x="56" y="23" width="11" height="7" rx="2"/>',
  module: '<rect x="17" y="28" width="56" height="11" rx="2.5"/><rect x="17" y="43" width="56" height="11" rx="2.5"/><rect x="17" y="58" width="56" height="11" rx="2.5"/><path d="M11 33.5h6M11 48.5h6M11 63.5h6"/>',
  motor: '<rect x="19" y="27" width="43" height="35" rx="7"/><path d="M29 27v35M40 27v35M51 27v35"/><path d="M62 38h11v13H62"/><path d="M19 44h-8"/>',
  inverter: '<rect x="15" y="31" width="53" height="30" rx="3"/><path d="M22 31v-7M32 31v-7M42 31v-7M52 31v-7M62 31v-7"/><path d="M68 46h9"/><circle cx="41" cy="46" r="7"/>',
  charger: '<rect x="13" y="31" width="40" height="29" rx="3"/><path d="M53 45h8"/><path d="M61 37h10v17H61z"/><path d="M71 42h5M71 49h5"/>',
  port: '<circle cx="45" cy="45" r="24"/><circle cx="36" cy="38" r="4.5"/><circle cx="54" cy="38" r="4.5"/><circle cx="36" cy="53" r="3.2"/><circle cx="54" cy="53" r="3.2"/><circle cx="45" cy="45" r="2.6"/>',
  controller: '<rect x="15" y="29" width="59" height="30" rx="3"/><rect x="25" y="37" width="19" height="14" rx="2"/><path d="M52 37h15M52 44h15M52 51h15"/><path d="M24 59v7M40 59v7M56 59v7"/>',
  conversion: '<path d="M18 32l27-12 27 12-27 12z"/><path d="M18 32v26l27 12V44"/><path d="M72 32v26L45 70V44"/><circle cx="45" cy="30" r="4"/><path d="M45 50v10"/>',
  cable: '<path d="M15 60c0-16 13-16 13-31"/><path d="M28 29c0 15 13 15 13 31"/><path d="M41 60c0-16 13-16 13-31"/><path d="M54 29c0 15 13 15 13 31"/><rect x="10" y="60" width="11" height="9" rx="2.5"/><rect x="61" y="60" width="11" height="9" rx="2.5"/>',
  ac: '<circle cx="29" cy="45" r="15"/><circle cx="29" cy="45" r="5"/><rect x="43" y="30" width="30" height="30" rx="9"/><path d="M58 30v-6M73 37h5"/>',
  engine: '<rect x="20" y="36" width="40" height="30" rx="3"/><rect x="27" y="26" width="26" height="10" rx="2"/><path d="M60 44h9v14h-9"/><path d="M20 52h-8"/><path d="M28 66v6M52 66v6"/>',
  gearbox: '<path d="M18 34h15l10-9v40l-10-9H18z"/><rect x="43" y="29" width="27" height="32" rx="6"/><path d="M57 29V17"/><circle cx="57" cy="14" r="3.6"/>',
  suspension: '<path d="M45 14v8"/><ellipse cx="45" cy="27" rx="16" ry="4.5"/><ellipse cx="45" cy="37" rx="16" ry="4.5"/><ellipse cx="45" cy="47" rx="16" ry="4.5"/><ellipse cx="45" cy="57" rx="16" ry="4.5"/><path d="M45 62v11"/>',
  brake: '<circle cx="40" cy="45" r="25"/><circle cx="40" cy="45" r="8"/><path d="M60 30h12v30H60c-4 0-7-3-7-7V37c0-4 3-7 7-7z"/>',
  wheel: '<circle cx="45" cy="45" r="26"/><circle cx="45" cy="45" r="9"/><path d="M45 19v8M45 63v8M19 45h8M63 45h8M27 27l6 6M57 57l6 6M63 27l-6 6M33 57l-6 6"/>',
  light: '<path d="M17 32h37c10 0 18 6 18 13s-8 13-18 13H17z"/><path d="M28 40c7 0 11 2 11 5s-4 5-11 5"/><circle cx="55" cy="45" r="7"/>',
  interior: '<path d="M31 18h14c4 0 6 3 6 6v25H31c-4 0-6-3-6-6V24c0-4 2-6 6-6z"/><path d="M25 49h33c5 0 8 3 8 8v8H33c-5 0-8-3-8-8z"/><path d="M51 32h9"/>',
  body: '<path d="M12 60l9-23h48l9 23z"/><path d="M28 37l-7 23M54 37l7 23"/><path d="M12 60h66"/>',
  electrical: '<rect x="22" y="24" width="46" height="26" rx="3"/><path d="M31 32v10M41 32v10M51 32v10M61 32v10"/><path d="M29 50v8M45 50v16M61 50v8"/><path d="M29 58h-9M61 58h9"/>',
  other: '<rect x="18" y="26" width="54" height="38" rx="4"/><path d="M18 38h54"/><path d="M30 26v-6M60 26v-6"/><circle cx="34" cy="51" r="4"/><path d="M46 51h18"/>',
}

export function partArt(category: PartCategory, tone: 1 | 2 | 3 = 1): string {
  const glyph = GLYPHS[category] ?? GLYPHS.other!
  const a = tone === 2 ? '#F2F5F2' : tone === 3 ? '#EBEFEB' : '#F0F2F0'
  const b = tone === 2 ? '#CBD3CC' : tone === 3 ? '#C3CCC5' : '#CCD2CD'
  return encode(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 90" width="360" height="360">` +
      `<defs><linearGradient id="g" x1="0" y1="0" x2=".3" y2="1"><stop offset="0" stop-color="${a}"/><stop offset="1" stop-color="${b}"/></linearGradient></defs>` +
      `<rect width="90" height="90" fill="url(#g)"/>` +
      `<g fill="none" stroke="#414B44" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">${glyph}</g>` +
    `</svg>`,
  )
}

export const PLACEHOLDER_CHOICES: readonly (readonly [PartCategory, string])[] = [
  ['battery', 'HV battery pack'], ['module', 'Battery modules'], ['motor', 'Traction motor'],
  ['inverter', 'Inverter / PDM'], ['charger', 'On-board charger'], ['port', 'Charge port'],
  ['controller', 'Control unit / ECU'], ['conversion', 'Conversion kit'],
  ['cable', 'HV cabling'], ['ac', 'A/C compressor'],
  ['engine', 'Engine'], ['gearbox', 'Gearbox'], ['suspension', 'Suspension'],
  ['brake', 'Brake disc'], ['wheel', 'Wheel'], ['light', 'Headlight'],
  ['interior', 'Seat / interior'], ['body', 'Body panel'], ['electrical', 'Fuse box / wiring'],
  ['other', 'Generic part'],
]

const CATEGORY_LABEL = new Map<string, string>(PLACEHOLDER_CHOICES.map(([k, l]) => [k, l]))

/**
 * The human name for a built-in category. Custom categories are not known here;
 * the catalogue store resolves those (and falls back to this for built-ins).
 */
export function categoryLabel(category: string | null | undefined): string {
  return (category && CATEGORY_LABEL.get(category)) || 'Part'
}

/** True for one of the built-in categories, as opposed to an admin-defined one. */
export function isBuiltinCategory(category: string | null | undefined): boolean {
  return !!category && CATEGORY_LABEL.has(category)
}

/* --------------------------- nothing yet ---------------------------- */

/**
 * Shown when no photo has been taken and no stand-in was chosen. It is
 * deliberately plain: a card that admits it has no picture is more honest than
 * one that borrows somebody else's, and it nudges whoever is adding stock to
 * go and photograph the thing.
 */
export function pendingArt(): string {
  return encode(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 90" width="360" height="360">` +
      `<rect width="90" height="90" fill="#EDEFED"/>` +
      `<g fill="none" stroke="#9AA39C" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">` +
        `<path d="M20 33h9l4-5h16l4 5h9a4 4 0 0 1 4 4v20a4 4 0 0 1-4 4H20a4 4 0 0 1-4-4V37a4 4 0 0 1 4-4z"/>` +
        `<circle cx="45" cy="47" r="9"/>` +
      `</g>` +
    `</svg>`,
  )
}

const PENDING = pendingArt()

/* ----------------------- choosing what to show ---------------------- */

/** What the viewer is actually looking at. Every surface labels this honestly. */
export type PictureKind = 'photo' | 'reference' | 'drawing' | 'pending'

export function pictureKind(src: string | null | undefined): PictureKind {
  if (src === PENDING) return 'pending'
  if (isIllustration(src)) return 'drawing'
  if (isStockPhoto(src)) return 'reference'
  return 'photo'
}

/** The short badge that sits on a card. A real photo needs no badge. */
export const PICTURE_BADGE: Record<PictureKind, string> = {
  photo: '',
  reference: 'Reference photo',
  drawing: 'Illustration',
  pending: 'Photo coming',
}

/**
 * In order: a photograph of the actual item, then a shipped reference photo of
 * that type of part, then a drawing. Only the first is the thing being sold,
 * which is why `isStandIn` exists and why every surface labels the other two.
 */
export function pictureFor(
  part: { photoUrls?: string[]; placeholder?: PartCategory | null; standIn?: string | null },
  /** A category's uploaded stand-in, used when nothing else is on the part. */
  fallback?: string | null,
): string {
  return (part.photoUrls ?? [])[0]
    ?? photoUrl(part.standIn)
    ?? (part.placeholder ? partArt(part.placeholder) : (fallback || PENDING))
}

export function vehiclePicture(
  vehicle: { body?: BodyType; photoUrls?: string[]; colour?: string; standIn?: string | null },
): string {
  return (vehicle.photoUrls ?? [])[0]
    || photoUrl(vehicle.standIn)
    || (vehicle.body ? carArt(vehicle.body, vehicle.colour) : PENDING)
}

/**
 * True when the picture is not of the item itself — either a drawing or one of
 * the shipped reference photos. A reference photo is the more dangerous of the
 * two, because it looks like a photograph, so this must stay honest.
 */
export function isStandIn(src: string | undefined | null): boolean {
  return pictureKind(src) !== 'photo'
}
